import { computed, effect, inject, Injector } from '@angular/core';
import { MIPORTAL_API } from '@core/api/api-clients';
import type { GetNotificationsQuery, NotificationDto } from '@core/api/miportal-api.types';
import { LoggerService } from '@core/services/logger.service.js';
import { fromApi } from '@mmscan/angular-openapi';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, pipe, switchMap, tap } from 'rxjs';
import { AuthStore } from './auth.store';

export type SeverityTab = 'all' | 'high' | 'medium' | 'low';

interface SeverityState {
  notifications: NotificationDto[];
  loading: boolean;
  hasMore: boolean;
  nextCursor?: string;
}

type NotificationsState = {
  notificationsBySeverity: Map<SeverityTab, SeverityState>;
  activeSeverityTab: SeverityTab;
  notificationsError: string | null;
  lastCheckedAt: Date | undefined;
  serverTotalCount: number;
  serverCountBySeverity: { high: number; medium: number; low: number };
};

const initialState: NotificationsState = {
  notificationsBySeverity: new Map([
    ['all', { notifications: [], loading: false, hasMore: true }],
    ['high', { notifications: [], loading: false, hasMore: true }],
    ['medium', { notifications: [], loading: false, hasMore: true }],
    ['low', { notifications: [], loading: false, hasMore: true }],
  ]),
  activeSeverityTab: 'all',
  notificationsError: null,
  lastCheckedAt: undefined,
  serverTotalCount: 0,
  serverCountBySeverity: { high: 0, medium: 0, low: 0 },
};

// Helper function for updating Map state immutably
function updateSeverityMap(
  map: Map<SeverityTab, SeverityState>,
  severity: SeverityTab,
  partialState: Partial<SeverityState>
): Map<SeverityTab, SeverityState> {
  const newMap = new Map(map);
  const currentState = newMap.get(severity);
  if (currentState) {
    newMap.set(severity, { ...currentState, ...partialState });
  }
  return newMap;
}

export const NotificationsStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed((state) => ({
    // Computed from active tab
    notifications: computed(() => {
      const activeTab = state.activeSeverityTab();
      const tabState = state.notificationsBySeverity().get(activeTab);
      return tabState?.notifications ?? [];
    }),

    notificationsLoading: computed(() => {
      const activeTab = state.activeSeverityTab();
      const tabState = state.notificationsBySeverity().get(activeTab);
      return tabState?.loading ?? false;
    }),

    hasMoreNotifications: computed(() => {
      const activeTab = state.activeSeverityTab();
      const tabState = state.notificationsBySeverity().get(activeTab);
      return tabState?.hasMore ?? false;
    }),

    // Formatted counts
    unreadNotificationsCount: computed(() => {
      const unreadCount = state.serverTotalCount();
      return unreadCount > 99 ? '99+' : unreadCount.toString();
    }),

    highUnreadCount: computed(() => state.serverCountBySeverity().high),
    mediumUnreadCount: computed(() => state.serverCountBySeverity().medium),
    lowUnreadCount: computed(() => state.serverCountBySeverity().low),
  })),

  withMethods((store) => {
    // Dependency injection
    const api = inject(MIPORTAL_API);
    const authStore = inject(AuthStore);
    const logger = inject(LoggerService);
    const injector = inject(Injector);

    // Polling state (outside of store state since it's imperative)
    const POLL_INTERVAL = 30_000; // 30 seconds
    const PAGE_LIMIT = 10;
    let pollingInterval: number | undefined;
    let visibilityChangeListener: (() => void) | undefined;
    let isInitialized = false;

    const methods = {
      // Load notifications for a specific severity tab
      loadNotifications: rxMethod<SeverityTab | void>(
        pipe(
          filter((severity) => {
            const targetSeverity = severity ?? store.activeSeverityTab();
            const currentState = store.notificationsBySeverity().get(targetSeverity);
            const userId = authStore.loggedUser()?.id;

            // Guard conditions
            if (!userId) {
              logger.warn('Cannot load notifications: no user logged in');
              return false;
            }
            if (!currentState) {
              logger.warn('Cannot load notifications: no state for severity', targetSeverity);
              return false;
            }
            if (currentState.loading) {
              logger.debug('Cannot load notifications: already loading');
              return false;
            }
            if (!currentState.hasMore) {
              logger.debug('Cannot load notifications: no more to load');
              return false;
            }

            return true;
          }),
          tap((severity) => {
            const targetSeverity = severity ?? store.activeSeverityTab();
            logger.info('Loading notifications for severity:', targetSeverity);

            // Update loading state
            patchState(store, {
              notificationsBySeverity: updateSeverityMap(store.notificationsBySeverity(), targetSeverity, {
                loading: true,
              }),
              notificationsError: null,
            });
          }),
          switchMap((severity) => {
            const targetSeverity = severity ?? store.activeSeverityTab();
            const currentState = store.notificationsBySeverity().get(targetSeverity)!;
            const userId = authStore.loggedUser()!.id;

            const filters: GetNotificationsQuery = {
              limit: PAGE_LIMIT,
              includeRead: 'true',
              ...(currentState.nextCursor && { cursor: currentState.nextCursor }),
              ...(targetSeverity !== 'all' && { severity: targetSeverity.toUpperCase() as 'HIGH' | 'MEDIUM' | 'LOW' }),
            };

            return fromApi(
              (signal) =>
                api.GET('/api/miportal/users/{userId}/notifications', {
                  params: { path: { userId }, query: filters },
                  signal,
                }),
              'load notifications'
            ).pipe(
              tapResponse({
                next: (response) => {
                  logger.info('response from notificationApi.loadMany:', response);

                  const existingNotifications = currentState.notifications;

                  patchState(store, {
                    notificationsBySeverity: updateSeverityMap(store.notificationsBySeverity(), targetSeverity, {
                      notifications: [...existingNotifications, ...response.data],
                      loading: false,
                      hasMore: response.hasMore,
                      nextCursor: response.nextCursor,
                    }),
                    lastCheckedAt: new Date(),
                  });
                },
                error: (error) => {
                  logger.error('Failed to load notifications:', error);
                  patchState(store, {
                    notificationsError: 'Failed to load notifications. Please try again.',
                    notificationsBySeverity: updateSeverityMap(store.notificationsBySeverity(), targetSeverity, {
                      loading: false,
                    }),
                  });
                },
              })
            );
          })
        )
      ),

      loadMoreNotifications(): void {
        const activeTab = store.activeSeverityTab();
        methods.loadNotifications(activeTab);
      },

      setActiveSeverity(severity: SeverityTab): void {
        patchState(store, { activeSeverityTab: severity });
        const state = store.notificationsBySeverity().get(severity);

        // Load notifications if not loaded yet for this severity
        if (state && state.notifications.length === 0 && !state.loading) {
          methods.loadNotifications(severity);
        }
      },

      loadNotificationsOnClick(): void {
        logger.info('User clicked notification bell, refreshing notifications');

        // Initialize polling if not already done
        if (!isInitialized) {
          logger.info('Initializing polling on first click');
          methods.initializePolling();
        }

        // Ensure polling is started (in case effect hasn't triggered)
        if (!pollingInterval && authStore.loggedUser()) {
          logger.info('Starting polling as a fallback after manual load');
          methods.startPolling(true); // Skip initial check; we refresh explicitly below
        }

        // Always pull the latest counts from the server, then reload the list from
        // scratch so opening the panel shows any notifications created since last time.
        methods.checkForNewNotifications();
        methods.refreshNotifications();
      },

      markNotificationAsRead: rxMethod<NotificationDto>(
        pipe(
          filter((notification) => !notification.readAt),
          tap((notification) => {
            // Optimistically update the notification across all severity tabs
            patchState(store, {
              notificationsBySeverity: new Map(
                Array.from(store.notificationsBySeverity().entries()).map(([severity, state]) => [
                  severity,
                  {
                    ...state,
                    notifications: state.notifications.map((n) => (n.id === notification.id ? { ...n } : n)),
                  },
                ])
              ),
            });
          }),
          switchMap((notification) => {
            const userId = authStore.loggedUser()?.id;
            if (!userId) throw new Error('No user ID');

            return fromApi(
              (signal) =>
                api.PATCH('/api/miportal/users/{userId}/notifications/{id}', {
                  params: { path: { userId, id: notification.id } },
                  signal,
                }),
              'mark the notification as read'
            ).pipe(
              tapResponse({
                next: () => {
                  logger.debug('Notification marked as read');
                },
                error: (error) => {
                  logger.error('Failed to mark notification as read:', error);
                  // Revert the optimistic update on error
                  patchState(store, {
                    notificationsBySeverity: new Map(
                      Array.from(store.notificationsBySeverity().entries()).map(([severity, state]) => [
                        severity,
                        {
                          ...state,
                          notifications: state.notifications.map((n) =>
                            n.id === notification.id ? { ...n, readAt: undefined } : n
                          ),
                        },
                      ])
                    ),
                  });
                },
              })
            );
          })
        )
      ),

      markAllAsRead: rxMethod<void>(
        pipe(
          tap(() => {
            const readAt = new Date().toISOString();

            // Optimistically mark every loaded notification as read and clear the counts
            patchState(store, {
              notificationsBySeverity: new Map(
                Array.from(store.notificationsBySeverity().entries()).map(([severity, state]) => [
                  severity,
                  {
                    ...state,
                    notifications: state.notifications.map((n) => (n.readAt ? n : { ...n, readAt })),
                  },
                ])
              ),
              serverTotalCount: 0,
              serverCountBySeverity: { high: 0, medium: 0, low: 0 },
            });
          }),
          switchMap(() => {
            const userId = authStore.loggedUser()?.id;
            if (!userId) throw new Error('No user ID');

            return fromApi(
              (signal) =>
                api.PATCH('/api/miportal/users/{userId}/notifications/read-all', {
                  params: { path: { userId } },
                  signal,
                }),
              'mark all notifications as read'
            ).pipe(
              tapResponse({
                next: () => {
                  logger.debug('All notifications marked as read');
                },
                error: (error) => {
                  logger.error('Failed to mark all notifications as read:', error);
                  // Re-sync from the server on failure
                  methods.checkForNewNotifications();
                  methods.refreshNotifications();
                },
              })
            );
          })
        )
      ),

      getUnreadCount(): number {
        const allNotifications = store.notificationsBySeverity().get('all')?.notifications ?? [];
        return allNotifications.filter((notification) => !notification.readAt).length;
      },

      getNotificationText(notification: NotificationDto, locale: string): string {
        return locale === 'EN_CA' ? notification.message.EN_CA : notification.message.FR_CA;
      },

      getNotificationTitle(notification: NotificationDto, locale: string): string {
        return locale === 'EN_CA' ? notification.title.EN_CA : notification.title.FR_CA;
      },

      checkForNewNotifications: rxMethod<void>(
        pipe(
          filter(() => {
            const userId = authStore.loggedUser()?.id;
            return !!userId;
          }),
          switchMap(() => {
            const lastChecked = store.lastCheckedAt();
            const userId = authStore.loggedUser()!.id;

            const since = lastChecked?.toISOString() ?? new Date().toISOString();

            return fromApi(
              (signal) =>
                api.GET('/api/miportal/users/{userId}/notifications/check', {
                  params: { path: { userId }, query: { since } },
                  signal,
                }),
              'check for notifications'
            ).pipe(
              tapResponse({
                next: (response) => {
                  logger.info('Check notifications response:', response);

                  // Get current count before updating
                  const currentTotalCount = store.serverTotalCount();

                  // Update counts from server
                  patchState(store, {
                    serverTotalCount: response.totalCount,
                    serverCountBySeverity: response.countBySeverity,
                  });

                  // Reload notifications if there are new ones or count changed
                  if (response.hasNew || response.totalCount !== currentTotalCount) {
                    methods.refreshNotifications();
                  }
                },
                error: (error) => {
                  logger.error('Failed to check for new notifications:', error);
                  // Continue polling despite error
                },
              })
            );
          })
        )
      ),

      refreshNotifications(): void {
        logger.info('Refreshing notifications due to new updates');

        // Reset pagination state for all tabs
        patchState(store, {
          notificationsBySeverity: new Map([
            ['all', { notifications: [], loading: false, hasMore: true }],
            ['high', { notifications: [], loading: false, hasMore: true }],
            ['medium', { notifications: [], loading: false, hasMore: true }],
            ['low', { notifications: [], loading: false, hasMore: true }],
          ]),
        });

        // Load fresh notifications for the active tab
        const activeTab = store.activeSeverityTab();
        methods.loadNotifications(activeTab);
      },

      cleanup(): void {
        patchState(store, {
          notificationsBySeverity: new Map([
            ['all', { notifications: [], loading: false, hasMore: true }],
            ['high', { notifications: [], loading: false, hasMore: true }],
            ['medium', { notifications: [], loading: false, hasMore: true }],
            ['low', { notifications: [], loading: false, hasMore: true }],
          ]),
          notificationsError: null,
          lastCheckedAt: undefined,
          activeSeverityTab: 'all',
          serverTotalCount: 0,
          serverCountBySeverity: { high: 0, medium: 0, low: 0 },
        });
      },

      // Polling management methods
      startPolling(skipInitialLoad = false): void {
        // Don't start if already polling
        if (pollingInterval) return;

        logger.info('Starting notification polling');

        // Initial check - only fetch counts, not notifications
        if (!skipInitialLoad) {
          methods.checkForNewNotifications();
        }

        // Setup polling interval
        pollingInterval = window.setInterval(() => {
          methods.checkForNewNotifications();
        }, POLL_INTERVAL);
      },

      stopPolling(): void {
        if (pollingInterval) {
          logger.info('Stopping notification polling');
          window.clearInterval(pollingInterval);
          pollingInterval = undefined;
        }
      },

      setupVisibilityListener(): void {
        visibilityChangeListener = () => {
          if (document.visibilityState === 'visible') {
            // Resume polling when tab becomes visible
            if (authStore.loggedUser() && !pollingInterval) {
              logger.info('Tab became visible, resuming polling');
              methods.startPolling();
            }
          } else {
            // Pause polling when tab is hidden (but only if already polling)
            if (pollingInterval) {
              logger.info('Tab hidden, pausing polling');
              methods.stopPolling();
            }
          }
        };

        document.addEventListener('visibilitychange', visibilityChangeListener);
      },

      destroyPolling(): void {
        methods.stopPolling();

        if (visibilityChangeListener) {
          document.removeEventListener('visibilitychange', visibilityChangeListener);
        }

        methods.cleanup();
      },

      // Initialize polling based on auth state
      initializePolling(): void {
        if (isInitialized) {
          logger.info('Polling already initialized, skipping');
          return;
        }

        isInitialized = true;
        logger.info('Initializing notification polling system');

        // Start/stop polling based on user login state
        effect(
          () => {
            const loggedUser = authStore.loggedUser();
            logger.info('NotificationsStore effect triggered, loggedUser:', loggedUser?.id);

            if (loggedUser) {
              logger.info('User is logged in, starting polling');
              methods.startPolling();
            } else {
              logger.info('User is not logged in, stopping polling');
              methods.stopPolling();
              methods.cleanup();
            }
          },
          { injector }
        );

        // Setup visibility change listener
        methods.setupVisibilityListener();
      },
    };

    // Auto-initialize on store creation
    methods.initializePolling();

    return methods;
  })
);
